import React from 'react';
import { createPortal } from 'react-dom';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocations } from '@/domain/events/useEvents';
import { dateOnlyToLocalDate, formatShortDate } from '@/domain/dates';
import { fromSiteInputValue, pluralize, siteZoneAbbreviation, toSiteInputValue } from '@/domain/warehouse';
import {
  createTransfer,
  fetchTransferEventOption,
  fetchTransferLineAvailability,
  fetchTransferVehicles,
  searchTransferEvents,
  transferCreateQueryKeys,
  type TransferCreateInput,
  type TransferEventOption,
  type TransferItemSearchResult,
  type TransferScope,
  type TransferTracking,
  type TransferVehicleOption,
} from '@/data/transferCreate';
import { useModalScrollLock } from '../../hooks/useModalScrollLock';
import useSessionStore from '../../state/session';
import { CloseIcon } from '../../ui/CloseIcon';
import { ModalActionError } from '../../ui/ModalActionError';
import { useToasts } from '../../ui/ToastProvider';
import { readableErrorMessage } from '../../utils/appErrors';
import { captureOpener, holdFocusOnBackdrop, trapTabKey } from '../receiving/dialogDom';
import TransferItemSearch, { SearchCombobox, useDebouncedText } from './TransferItemSearch';
import './NewTransferDialog.css';

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

export type NewTransferInitialLine = {
  /** Explicit units from an exact-asset workflow; the writer rechecks custody. */
  assets?: readonly { id: string; label: string }[];
  itemId?: string | null;
  productId?: string | null;
  qty: number;
  name?: string | null;
  sku?: string | null;
  /** The event line this transfer line covers. */
  eventLineId?: string | null;
};

export type NewTransferInitial = {
  fromLocationId?: string | null;
  toLocationId?: string | null;
  eventId?: string | null;
  /** Shown on the event chip while the event loads. */
  eventCode?: string | null;
  lines?: readonly NewTransferInitialLine[];
  /** ISO timestamp or YYYY-MM-DD. Defaults to today. */
  shipAt?: string | null;
  /** ISO timestamp; shown on the To site's clock. */
  arriveBy?: string | null;
  /** ISO timestamp or YYYY-MM-DD; used while the transfer is Temporary. */
  returnBy?: string | null;
};

export type NewTransferDialogProps = {
  open: boolean;
  onClose: () => void;
  onCreated: (transferId: string, docNo: string | null) => void;
  /** Prefill, read when the dialog opens (the event workspace passes its lines, site and event). */
  initial?: NewTransferInitial | null;
};

/* ------------------------------------------------------------------ */
/*  Draft lines                                                        */
/* ------------------------------------------------------------------ */

type LineKind = 'item' | 'product' | 'vehicle' | 'external';

type DraftAsset = { id: string; label: string };

type DraftLine = {
  key: string;
  kind: LineKind;
  itemId: string | null;
  productId: string | null;
  vehicleId: string | null;
  /** The site a vehicle line was chosen at; it must stay the From site. */
  vehicleSiteId: string | null;
  name: string;
  sku: string | null;
  detail: string | null;
  accessory: boolean;
  tracking: TransferTracking | null;
  qty: number;
  assets: readonly DraftAsset[];
  /** The free count the search showed, valid while `freeKey` matches the current site and window. */
  searchFree: number | null;
  freeKey: string | null;
  eventLineId: string | null;
};

const MAX_QTY = 100000;
const NO_EVENTS: TransferEventOption[] = [];
let lineCounter = 0;

function nextLineKey(): string {
  lineCounter += 1;
  return `line-${lineCounter}`;
}

function clampQty(value: number, min = 1): number {
  if (!Number.isFinite(value)) return min;
  return Math.min(MAX_QTY, Math.max(min, Math.trunc(value)));
}

function initialLines(lines: readonly NewTransferInitialLine[] | null | undefined): DraftLine[] {
  return (lines ?? [])
    .filter((line) => line.itemId || line.productId)
    .map((line) => ({
      key: nextLineKey(),
      kind: line.itemId ? 'item' : 'product',
      itemId: line.itemId ?? null,
      productId: line.productId ?? null,
      vehicleId: null,
      vehicleSiteId: null,
      name: line.name?.trim() || line.sku?.trim() || 'Item',
      sku: line.sku?.trim() || null,
      detail: null,
      accessory: false,
      tracking: line.assets?.length ? 'serial' : null,
      qty: clampQty(Math.max(Number(line.qty), line.assets?.length ?? 0)),
      assets: line.assets ?? [],
      searchFree: null,
      freeKey: null,
      eventLineId: line.eventLineId ?? null,
    }));
}

function matchesResult(line: DraftLine, result: TransferItemSearchResult): boolean {
  return line.kind === 'item' && line.itemId === result.itemId;
}

/** A picked match joins the line for the same item (one more unit, or one more chosen serial). */
function addSearchResult(lines: readonly DraftLine[], result: TransferItemSearchResult, freeKey: string): DraftLine[] {
  const asset = result.asset
    ? { id: result.asset.id, label: result.asset.serial ?? result.asset.barcode ?? 'Serial' }
    : null;
  const free = result.freeAtFrom;
  const index = lines.findIndex((line) => matchesResult(line, result));
  if (index >= 0) {
    const line = lines[index];
    if (asset && line.assets.some((chosen) => chosen.id === asset.id)) return [...lines];
    const assets = asset ? [...line.assets, asset] : line.assets;
    const updated: DraftLine = {
      ...line,
      assets,
      qty: asset ? clampQty(Math.max(line.qty, assets.length)) : clampQty(line.qty + 1),
      tracking: result.tracking ?? line.tracking,
      sku: line.sku ?? result.sku,
      searchFree: free ?? line.searchFree,
      freeKey: free != null ? freeKey : line.freeKey,
    };
    return lines.map((candidate, position) => (position === index ? updated : candidate));
  }
  return [
    ...lines,
    {
      key: nextLineKey(),
      kind: 'item',
      itemId: result.itemId,
      productId: null,
      vehicleId: null,
      vehicleSiteId: null,
      name: result.name,
      sku: result.sku,
      detail: null,
      accessory: false,
      tracking: result.tracking,
      qty: asset ? 1 : clampQty(result.suggestedQty ?? 1),
      assets: asset ? [asset] : [],
      searchFree: free,
      freeKey: free != null ? freeKey : null,
      eventLineId: null,
    },
  ];
}

function lineDetail(line: DraftLine): string | null {
  if (line.kind === 'external') {
    return [line.sku, line.accessory ? 'Accessory' : 'Not in the catalog'].filter(Boolean).join(' · ');
  }
  if (line.kind === 'vehicle') return line.detail;
  return line.sku;
}

function serialsText(line: DraftLine): string {
  if (line.kind === 'vehicle') return 'One vehicle';
  if (line.kind === 'external') return 'By quantity';
  if (line.assets.length > 0) {
    const labels = line.assets.map((asset) => asset.label);
    const chosen = labels.length <= 2 ? labels.join(', ') : `${labels[0]} + ${labels.length - 1} more`;
    return `${chosen} chosen`;
  }
  if (line.tracking === 'serial') return 'Scanned when picked';
  if (line.tracking === 'quantity') return 'By quantity';
  // Prefilled lines don't say how the item is tracked; picking decides.
  return 'Set at picking';
}

/* ------------------------------------------------------------------ */
/*  Dates                                                              */
/* ------------------------------------------------------------------ */

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

function localDay(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function toDateInputValue(value: string | null | undefined): string {
  const text = value?.trim() ?? '';
  if (!text) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;
  const date = new Date(text);
  return Number.isNaN(date.getTime()) ? '' : localDay(date);
}

/** A date-only field is stored at local noon, so the calendar day holds in every US site's time zone. */
function dayToIso(day: string): string | null {
  return dateOnlyToLocalDate(day)?.toISOString() ?? null;
}

function addOneDay(iso: string): string {
  return new Date(new Date(iso).getTime() + 24 * 60 * 60 * 1000).toISOString();
}

function siteShortName(name: string | null | undefined): string | null {
  const text = name?.trim() ?? '';
  if (!text) return null;
  return text.replace(/^\d+\s*-\s*/, '') || text;
}

function uniqueIds(values: ReadonlyArray<string | null>): string[] {
  return Array.from(new Set(values.filter((value): value is string => Boolean(value))));
}

/* ------------------------------------------------------------------ */
/*  Small parts                                                        */
/* ------------------------------------------------------------------ */

type QtyStepperProps = {
  value: number;
  min: number;
  label: string;
  disabled: boolean;
  onChange: (next: number) => void;
};

/** − n +, with the number typeable; a typed value commits on blur or Enter. */
function QtyStepper({ value, min, label, disabled, onChange }: QtyStepperProps) {
  const [draft, setDraft] = React.useState(String(value));
  const [editing, setEditing] = React.useState(false);

  React.useEffect(() => {
    if (!editing) setDraft(String(value));
  }, [editing, value]);

  function commit() {
    setEditing(false);
    const parsed = Number.parseInt(draft, 10);
    const next = Number.isFinite(parsed) ? clampQty(parsed, min) : value;
    setDraft(String(next));
    if (next !== value) onChange(next);
  }

  function handleDraftChange(event: React.ChangeEvent<HTMLInputElement>) {
    setDraft(event.target.value.replace(/[^0-9]/g, '').slice(0, 6));
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter') {
      event.preventDefault();
      commit();
    }
  }

  return (
    <span className="new-transfer__stepper" data-disabled={disabled ? 'true' : undefined}>
      <button
        type="button"
        aria-label={`One fewer ${label}`}
        disabled={disabled || value <= min}
        onClick={() => onChange(clampQty(value - 1, min))}
      >
        −
      </button>
      <input
        type="text"
        inputMode="numeric"
        aria-label={`Quantity of ${label}`}
        value={draft}
        disabled={disabled}
        onFocus={() => setEditing(true)}
        onChange={handleDraftChange}
        onBlur={commit}
        onKeyDown={handleKeyDown}
      />
      <button
        type="button"
        aria-label={`One more ${label}`}
        disabled={disabled || value >= MAX_QTY}
        onClick={() => onChange(clampQty(value + 1, min))}
      >
        +
      </button>
    </span>
  );
}

function eventTitle(event: TransferEventOption): string {
  return [event.code, event.name].filter(Boolean).join(' ') || 'Linked event';
}

function eventMeta(event: TransferEventOption, siteNames: ReadonlyMap<string, string>): string {
  return [
    event.startDate ? formatShortDate(event.startDate) : null,
    event.siteLocationId ? siteNames.get(event.siteLocationId) ?? null : null,
  ].filter(Boolean).join(' · ');
}

function eventKey(event: TransferEventOption): string {
  return event.id;
}

type EventPickerProps = {
  siteNames: ReadonlyMap<string, string>;
  onPick: (event: TransferEventOption) => void;
};

function EventPicker({ siteNames, onPick }: EventPickerProps) {
  const [text, setText] = React.useState('');
  const term = useDebouncedText(text);
  const typing = text.trim() !== term;
  const searchable = term.length >= 2;
  const eventsQuery = useQuery({
    queryKey: transferCreateQueryKeys.eventSearch(term),
    queryFn: () => searchTransferEvents({ term }),
    enabled: searchable,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
  const options = React.useMemo(
    () => (searchable && eventsQuery.data ? eventsQuery.data : NO_EVENTS),
    [eventsQuery.data, searchable],
  );
  const loading = searchable && (typing || eventsQuery.isFetching);

  let status: string | null = null;
  if (text.trim().length > 0 && text.trim().length < 2) status = 'Type at least 2 characters.';
  else if (searchable && eventsQuery.isError) status = 'Event search is not available right now. Try again.';
  else if (searchable && !loading && options.length === 0 && eventsQuery.isSuccess) status = `No events match “${term}”.`;
  else if (loading && options.length === 0) status = 'Searching…';

  const renderOption = React.useCallback((event: TransferEventOption) => (
    <span className="transfer-search__option-main">
      <strong>{eventTitle(event)}</strong>
      <small>{eventMeta(event, siteNames) || 'No date or site yet'}</small>
    </span>
  ), [siteNames]);

  const handleSelect = React.useCallback((event: TransferEventOption) => {
    setText('');
    onPick(event);
  }, [onPick]);

  return (
    <SearchCombobox
      className="new-transfer__event-search"
      label="Find an event by number or name"
      placeholder="Event number or name"
      value={text}
      onValueChange={setText}
      options={options}
      optionKey={eventKey}
      renderOption={renderOption}
      onSelect={handleSelect}
      listLabel="Matching events"
      listEnabled={text.trim().length > 0}
      status={status}
      announcement={loading ? '' : options.length > 0 ? `${options.length} ${options.length === 1 ? 'event' : 'events'} found.` : status ?? ''}
      busy={loading}
    />
  );
}

type ExternalLineFormProps = {
  onAdd: (line: { name: string; code: string | null; qty: number; accessory: boolean }) => void;
  onCancel: () => void;
};

/** A line for something that is not in the catalog (an external item or an accessory). */
function ExternalLineForm({ onAdd, onCancel }: ExternalLineFormProps) {
  const headingId = React.useId();
  const nameId = React.useId();
  const codeId = React.useId();
  const qtyId = React.useId();
  const kindId = React.useId();
  const nameRef = React.useRef<HTMLInputElement>(null);
  const [name, setName] = React.useState('');
  const [code, setCode] = React.useState('');
  const [qty, setQty] = React.useState('1');
  const [kind, setKind] = React.useState('external');
  const [problem, setProblem] = React.useState<string | null>(null);

  React.useEffect(() => {
    nameRef.current?.focus({ preventScroll: true });
  }, []);

  function add() {
    const trimmed = name.trim();
    if (!trimmed) {
      setProblem('Enter a name for the item.');
      nameRef.current?.focus();
      return;
    }
    onAdd({ name: trimmed, code: code.trim() || null, qty: clampQty(Number.parseInt(qty, 10)), accessory: kind === 'accessory' });
    setName('');
    setCode('');
    setQty('1');
    setProblem(null);
    nameRef.current?.focus();
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter') {
      event.preventDefault();
      add();
    }
  }

  function handleNameChange(event: React.ChangeEvent<HTMLInputElement>) {
    setName(event.target.value);
    if (problem) setProblem(null);
  }

  return (
    <div className="new-transfer__extra" role="group" aria-labelledby={headingId}>
      <div className="new-transfer__extra-head">
        <h4 id={headingId}>Item not in the catalog</h4>
        <button type="button" className="btn-close" aria-label="Cancel" onClick={onCancel}>
          <CloseIcon />
        </button>
      </div>
      <div className="new-transfer__row new-transfer__row--extra">
        <div className="new-transfer__field">
          <label htmlFor={nameId}>Name</label>
          <input id={nameId} ref={nameRef} type="text" value={name} onChange={handleNameChange} onKeyDown={handleKeyDown} />
        </div>
        <div className="new-transfer__field">
          <label htmlFor={codeId}>Code (optional)</label>
          <input id={codeId} type="text" value={code} onChange={(event) => setCode(event.target.value)} onKeyDown={handleKeyDown} />
        </div>
        <div className="new-transfer__field">
          <label htmlFor={qtyId}>Qty</label>
          <input
            id={qtyId}
            type="text"
            inputMode="numeric"
            value={qty}
            onChange={(event) => setQty(event.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
            onKeyDown={handleKeyDown}
          />
        </div>
        <div className="new-transfer__field">
          <label htmlFor={kindId}>Type</label>
          <select id={kindId} value={kind} onChange={(event) => setKind(event.target.value)}>
            <option value="external">External item</option>
            <option value="accessory">Accessory</option>
          </select>
        </div>
      </div>
      {problem ? <p className="new-transfer__extra-problem" role="alert">{problem}</p> : null}
      <div className="new-transfer__extra-actions">
        <button type="button" className="btn sm btn-primary" onClick={add}>Add line</button>
      </div>
    </div>
  );
}

type VehicleLineFormProps = {
  fromLocationId: string | null;
  fromSiteName: string | null;
  chosenIds: ReadonlySet<string>;
  onAdd: (vehicle: TransferVehicleOption) => void;
  onCancel: () => void;
};

/** A fleet vehicle that is at the From site. */
function VehicleLineForm({ fromLocationId, fromSiteName, chosenIds, onAdd, onCancel }: VehicleLineFormProps) {
  const headingId = React.useId();
  const selectId = React.useId();
  const [vehicleId, setVehicleId] = React.useState('');
  const vehiclesQuery = useQuery({
    queryKey: transferCreateQueryKeys.vehicles(fromLocationId ?? ''),
    queryFn: () => fetchTransferVehicles(fromLocationId ?? ''),
    enabled: Boolean(fromLocationId),
    staleTime: 60_000,
  });
  const vehicles = React.useMemo(
    () => (vehiclesQuery.data ?? []).filter((vehicle) => !chosenIds.has(vehicle.id)),
    [chosenIds, vehiclesQuery.data],
  );

  function add() {
    const vehicle = vehicles.find((candidate) => candidate.id === vehicleId);
    if (!vehicle) return;
    onAdd(vehicle);
    setVehicleId('');
  }

  let body: React.ReactNode;
  if (!fromLocationId) body = <p className="new-transfer__extra-note">Choose the From site to list its vehicles.</p>;
  else if (vehiclesQuery.isPending) body = <p className="new-transfer__extra-note">Loading vehicles…</p>;
  else if (vehiclesQuery.isError) body = <p className="new-transfer__extra-problem" role="alert">Vehicles could not be loaded. Try again.</p>;
  else if (vehicles.length === 0) body = <p className="new-transfer__extra-note">No other active vehicles are at {fromSiteName ?? 'the From site'}.</p>;
  else {
    body = (
      <div className="new-transfer__row new-transfer__row--vehicle">
        <div className="new-transfer__field">
          <label htmlFor={selectId}>Vehicle</label>
          <select id={selectId} value={vehicleId} onChange={(event) => setVehicleId(event.target.value)}>
            <option value="">Choose a vehicle</option>
            {vehicles.map((vehicle) => (
              <option key={vehicle.id} value={vehicle.id}>
                {[vehicle.name, vehicle.type, vehicle.plate].filter(Boolean).join(' · ')}
              </option>
            ))}
          </select>
        </div>
      </div>
    );
  }

  return (
    <div className="new-transfer__extra" role="group" aria-labelledby={headingId}>
      <div className="new-transfer__extra-head">
        <h4 id={headingId}>Vehicle</h4>
        <button type="button" className="btn-close" aria-label="Cancel" onClick={onCancel}>
          <CloseIcon />
        </button>
      </div>
      {body}
      {fromLocationId && vehicles.length > 0 ? (
        <div className="new-transfer__extra-actions">
          <button type="button" className="btn sm btn-primary" disabled={!vehicleId} onClick={add}>Add vehicle</button>
        </div>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  The dialog                                                         */
/* ------------------------------------------------------------------ */

type PanelProps = Omit<NewTransferDialogProps, 'open'>;
type ExtraPanel = 'none' | 'external' | 'vehicle';
type DialogError = { title: string; message: string; hint?: string };

function NewTransferDialogPanel({ onClose, onCreated, initial }: PanelProps) {
  const queryClient = useQueryClient();
  const { push } = useToasts();
  const homeLocationId = useSessionStore((state) => state.profile?.home_location_id ?? null);

  const titleId = React.useId();
  const leadId = React.useId();
  const routeHeadingId = React.useId();
  const datesHeadingId = React.useId();
  const eventHeadingId = React.useId();
  const itemsHeadingId = React.useId();
  const typeLabelId = React.useId();
  const fromId = React.useId();
  const toId = React.useId();
  const shipId = React.useId();
  const arriveId = React.useId();
  const arriveZoneId = React.useId();
  const returnId = React.useId();

  const dialogRef = React.useRef<HTMLDivElement>(null);
  const fromSelectRef = React.useRef<HTMLSelectElement>(null);
  const toSelectRef = React.useRef<HTMLSelectElement>(null);
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const [opener] = React.useState(captureOpener);
  const mountedRef = React.useRef(true);
  const requestRef = React.useRef<{ key: string; signature: string } | null>(null);

  const [fromLocationId, setFromLocationId] = React.useState(() => {
    const to = initial?.toLocationId ?? '';
    if (initial?.fromLocationId) return initial.fromLocationId;
    return homeLocationId && homeLocationId !== to ? homeLocationId : '';
  });
  const [toLocationId, setToLocationId] = React.useState(() => initial?.toLocationId ?? '');
  const [scope, setScope] = React.useState<TransferScope>('temporary');
  const [shipDay, setShipDay] = React.useState(() => toDateInputValue(initial?.shipAt) || localDay(new Date()));
  // What the person typed in Arrive by, on the To site's clock; null until they type (then the
  // prefilled time is shown on that clock, whichever site is chosen).
  const [arriveTyped, setArriveTyped] = React.useState<string | null>(null);
  const [returnDay, setReturnDay] = React.useState(() => toDateInputValue(initial?.returnBy));
  const [eventId, setEventId] = React.useState<string | null>(() => initial?.eventId ?? null);
  const [lines, setLines] = React.useState<DraftLine[]>(() => initialLines(initial?.lines));
  const [panel, setPanel] = React.useState<ExtraPanel>('none');
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<DialogError | null>(null);
  // Read once: where focus starts, and the code shown while the prefilled event loads.
  const [firstFocus] = React.useState(() => (!fromLocationId ? 'from' : !toLocationId ? 'to' : 'search'));
  const [seedEvent] = React.useState(() => (initial?.eventId ? { id: initial.eventId, code: initial.eventCode ?? null } : null));
  const [todayNoon] = React.useState(() => dayToIso(localDay(new Date())) ?? new Date().toISOString());

  useModalScrollLock(true);

  React.useEffect(() => {
    mountedRef.current = true;
    const frame = window.requestAnimationFrame(() => {
      const target = firstFocus === 'from'
        ? fromSelectRef.current
        : firstFocus === 'to'
          ? toSelectRef.current
          : searchInputRef.current;
      (target ?? dialogRef.current)?.focus({ preventScroll: true });
    });
    return () => {
      mountedRef.current = false;
      window.cancelAnimationFrame(frame);
      if (opener?.isConnected) opener.focus({ preventScroll: true });
    };
  }, [firstFocus, opener]);

  /* Sites */
  const locationsQuery = useLocations({ allowedLocationIds: null });
  const sites = React.useMemo(
    () => [...(locationsQuery.data ?? [])].sort((left, right) => left.name.localeCompare(right.name, undefined, { numeric: true })),
    [locationsQuery.data],
  );
  const siteNames = React.useMemo(() => new Map(sites.map((site) => [site.location_id, site.name])), [sites]);
  const fromSiteName = siteShortName(siteNames.get(fromLocationId));
  // Arrive by is on the To site's clock (as the record shows it), not the browser's.
  const toZone = sites.find((site) => site.location_id === toLocationId)?.timezone ?? null;
  const toZoneLabel = toLocationId ? siteZoneAbbreviation(toZone) : null;
  const arriveBy = arriveTyped ?? toSiteInputValue(initial?.arriveBy ?? null, toZone);

  const renderSiteOptions = (selected: string) => (
    <>
      <option value="">{locationsQuery.isPending ? 'Loading sites…' : 'Choose a site'}</option>
      {selected && !siteNames.has(selected) && !locationsQuery.isPending ? (
        <option value={selected}>Site not in the list</option>
      ) : null}
      {sites.map((site) => (
        <option key={site.location_id} value={site.location_id}>{site.name}</option>
      ))}
    </>
  );

  /* Event */
  const eventQuery = useQuery({
    queryKey: transferCreateQueryKeys.event(eventId ?? ''),
    queryFn: () => fetchTransferEventOption(eventId ?? ''),
    enabled: Boolean(eventId),
    staleTime: 5 * 60_000,
  });
  const linkedEvent: TransferEventOption | null = eventId
    ? eventQuery.data ?? {
      id: eventId,
      code: seedEvent?.id === eventId ? seedEvent.code : null,
      name: null,
      startDate: null,
      siteLocationId: null,
    }
    : null;

  const handleEventPick = React.useCallback((event: TransferEventOption) => {
    queryClient.setQueryData(transferCreateQueryKeys.event(event.id), event);
    setEventId(event.id);
    setToLocationId((current) => {
      if (current || !event.siteLocationId || event.siteLocationId === fromLocationId) return current;
      return event.siteLocationId;
    });
  }, [fromLocationId, queryClient]);

  /* Window for free counts: Ship → Return by, or one day from Ship (search_transfer_items' own
     default), so the search and the lines read the same snapshot for the same window. */
  const shipAtIso = React.useMemo(() => dayToIso(shipDay), [shipDay]);
  const arriveByIso = React.useMemo(() => (arriveBy ? fromSiteInputValue(arriveBy, toZone) : null), [arriveBy, toZone]);
  const returnAtIso = React.useMemo(() => (scope === 'temporary' ? dayToIso(returnDay) : null), [returnDay, scope]);
  const windowStart = shipAtIso ?? todayNoon;
  const windowEnd = returnAtIso && returnAtIso > windowStart ? returnAtIso : addOneDay(windowStart);
  const searchWindow = React.useMemo(() => ({ shipAt: windowStart, returnAt: windowEnd }), [windowEnd, windowStart]);
  const freeKey = `${fromLocationId}|${windowStart}|${windowEnd}`;
  // Typing a date changes the window once per digit; counts load once it settles.
  const settledFreeKey = useDebouncedText(freeKey, 400);
  const windowSettled = settledFreeKey === freeKey;

  const staleItemIds = React.useMemo(
    () => uniqueIds(lines
      .filter((line) => line.itemId && (line.searchFree == null || line.freeKey !== freeKey))
      .map((line) => line.itemId)),
    [freeKey, lines],
  );
  const availabilityQuery = useQuery({
    queryKey: transferCreateQueryKeys.lineAvailability(fromLocationId, windowStart, windowEnd, staleItemIds),
    queryFn: () => fetchTransferLineAvailability({
      fromLocationId,
      startAt: windowStart,
      endAt: windowEnd,
      itemIds: staleItemIds,
    }),
    enabled: windowSettled && Boolean(fromLocationId) && staleItemIds.length > 0,
    staleTime: 60_000,
  });

  function freeFor(line: DraftLine): { value: number | null; loading: boolean } {
    if (!line.itemId || !fromLocationId) return { value: null, loading: false };
    if (line.searchFree != null && line.freeKey === freeKey) return { value: line.searchFree, loading: false };
    const free = availabilityQuery.data;
    if (free && Object.prototype.hasOwnProperty.call(free, line.itemId)) return { value: free[line.itemId], loading: false };
    return { value: null, loading: !windowSettled || availabilityQuery.isFetching };
  }

  /* Lines */
  const handlePick = React.useCallback((result: TransferItemSearchResult) => {
    setLines((current) => addSearchResult(current, result, freeKey));
  }, [freeKey]);

  const handleExternalAdd = React.useCallback((line: { name: string; code: string | null; qty: number; accessory: boolean }) => {
    setLines((current) => [...current, {
      key: nextLineKey(),
      kind: 'external',
      itemId: null,
      productId: null,
      vehicleId: null,
      vehicleSiteId: null,
      name: line.name,
      sku: line.code,
      detail: null,
      accessory: line.accessory,
      tracking: 'quantity',
      qty: clampQty(line.qty),
      assets: [],
      searchFree: null,
      freeKey: null,
      eventLineId: null,
    }]);
  }, []);

  const handleVehicleAdd = React.useCallback((vehicle: TransferVehicleOption) => {
    setLines((current) => [...current, {
      key: nextLineKey(),
      kind: 'vehicle',
      itemId: null,
      productId: null,
      vehicleId: vehicle.id,
      vehicleSiteId: vehicle.locationId,
      name: vehicle.name,
      sku: null,
      detail: [vehicle.type, vehicle.plate].filter(Boolean).join(' · ') || null,
      accessory: false,
      tracking: null,
      qty: 1,
      assets: [],
      searchFree: null,
      freeKey: null,
      eventLineId: null,
    }]);
  }, []);

  const chosenVehicleIds = React.useMemo(
    () => new Set(lines.map((line) => line.vehicleId).filter((id): id is string => Boolean(id))),
    [lines],
  );

  function setLineQty(key: string, qty: number) {
    setLines((current) => current.map((line) => (line.key === key ? { ...line, qty } : line)));
  }

  function removeLine(key: string) {
    setLines((current) => current.filter((line) => line.key !== key));
  }

  /* Validation and submit */
  function validate(): string | null {
    if (!fromLocationId || !toLocationId) return 'Choose the From and To sites.';
    if (fromLocationId === toLocationId) return 'From and To must be different sites.';
    if (lines.length === 0) return 'Add at least one item.';
    if (lines.some((line) => line.kind === 'vehicle' && line.vehicleSiteId !== fromLocationId)) {
      return 'A vehicle on this transfer is not at the From site. Remove it, or change From back.';
    }
    if (arriveBy && shipDay && arriveBy.slice(0, 10) < shipDay) return 'Arrive by can’t be before the ship date.';
    if (scope === 'temporary' && returnDay && shipDay && returnDay < shipDay) return 'Return by can’t be before the ship date.';
    return null;
  }

  async function handleCreate() {
    if (submitting) return;
    const problem = validate();
    if (problem) {
      setError({ title: 'Check the transfer', message: problem });
      return;
    }
    const input: TransferCreateInput = {
      fromLocationId,
      toLocationId,
      scope,
      needByAt: arriveByIso,
      expectedShipAt: shipAtIso,
      expectedReturnAt: returnAtIso,
      eventId,
      lines: lines.map((line) => ({
        itemId: line.itemId,
        productId: line.productId,
        vehicleId: line.vehicleId,
        externalName: line.kind === 'external' ? line.name : null,
        externalCode: line.kind === 'external' ? line.sku : null,
        accessory: line.accessory,
        qty: line.qty,
        assetIds: line.assets.map((asset) => asset.id),
        eventLineId: line.eventLineId,
      })),
    };
    setSubmitting(true);
    setError(null);
    try {
      // One request key per submission; a retry of the same details reuses it, so it can't create twice.
      const signature = JSON.stringify(input);
      if (!requestRef.current || requestRef.current.signature !== signature) {
        requestRef.current = { key: crypto.randomUUID(), signature };
      }
      const result = await createTransfer(requestRef.current.key, input);
      void queryClient.invalidateQueries({ queryKey: transferCreateQueryKeys.root });
      void queryClient.invalidateQueries({ queryKey: ['transfer-orders'] });
      push({ type: 'success', message: result.docNo ? `Transfer ${result.docNo} created.` : 'Transfer created.' });
      if (!mountedRef.current) return;
      onCreated(result.transferId, result.docNo);
      onClose();
    } catch (createError) {
      if (!mountedRef.current) return;
      setError({
        title: 'The transfer was not created',
        message: readableErrorMessage(createError, 'The transfer was not created. Try again.', 'mutation'),
        hint: 'Your transfer details are still here, so you can try again.',
      });
    } finally {
      if (mountedRef.current) setSubmitting(false);
    }
  }

  /* Keyboard: Escape closes, Tab stays inside, and no key reaches the page behind the dialog
     (the event items grid deletes selected rows on Delete or Backspace). */
  function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Escape' && !event.defaultPrevented) {
      event.preventDefault();
      onClose();
    } else if (event.key === 'Tab' && dialogRef.current) {
      trapTabKey(event, dialogRef.current);
    }
    event.stopPropagation();
  }

  function handleOverlayMouseDown(event: React.MouseEvent<HTMLDivElement>) {
    holdFocusOnBackdrop(event);
    event.stopPropagation();
  }

  function stopPropagation(event: React.SyntheticEvent) {
    event.stopPropagation();
  }

  /* Footer */
  const units = lines.reduce((sum, line) => sum + (line.kind === 'vehicle' ? 1 : line.qty), 0);
  const summaryParts = [
    lines.length > 0 ? `${pluralize(lines.length, 'line')} · ${pluralize(units, 'unit')} so far` : 'No items yet',
  ];
  if (scope === 'temporary') {
    summaryParts.push(returnDay
      ? `a return leg is created for ${formatShortDate(returnDay)}`
      : 'add a Return by date for the return leg');
  }

  return (
    <div
      className="modal-overlay new-transfer-overlay"
      onMouseDown={handleOverlayMouseDown}
      onPointerDown={stopPropagation}
      onClick={stopPropagation}
    >
      <div
        ref={dialogRef}
        className="modal new-transfer"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={leadId}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
      >
        <header className="modal__header transfer-dialog__header">
          <span className="transfer-dialog__eyebrow">Warehouse</span>
          <h2 id={titleId} className="modal__title transfer-dialog__title">New transfer</h2>
          <p id={leadId} className="transfer-dialog__lead">
            Move items between sites. Find items by name, SKU or serial; availability is shown for the From site.
          </p>
          <button type="button" className="btn-close" aria-label="Close" onClick={onClose}>
            <CloseIcon />
          </button>
        </header>

        <div className="modal__body">
          <div className="modal__body-content new-transfer__body">
            <section className="new-transfer__section" aria-labelledby={routeHeadingId}>
              <h3 id={routeHeadingId} className="new-transfer__section-title">Route</h3>
              <div className="new-transfer__row">
                <div className="new-transfer__field">
                  <label htmlFor={fromId}>From</label>
                  <select
                    id={fromId}
                    ref={fromSelectRef}
                    value={fromLocationId}
                    onChange={(event) => setFromLocationId(event.target.value)}
                  >
                    {renderSiteOptions(fromLocationId)}
                  </select>
                </div>
                <div className="new-transfer__field">
                  <label htmlFor={toId}>To</label>
                  <select
                    id={toId}
                    ref={toSelectRef}
                    value={toLocationId}
                    onChange={(event) => setToLocationId(event.target.value)}
                  >
                    {renderSiteOptions(toLocationId)}
                  </select>
                </div>
                <div className="new-transfer__field">
                  <span id={typeLabelId} className="new-transfer__label">Type</span>
                  <div className="new-transfer__seg" role="group" aria-labelledby={typeLabelId}>
                    <button type="button" aria-pressed={scope === 'temporary'} onClick={() => setScope('temporary')}>
                      Temporary
                    </button>
                    <button type="button" aria-pressed={scope === 'permanent'} onClick={() => setScope('permanent')}>
                      Permanent
                    </button>
                  </div>
                </div>
              </div>
              {locationsQuery.isError ? (
                <p className="new-transfer__note" role="alert">Sites could not be loaded. Close the dialog and try again.</p>
              ) : null}
            </section>

            <section className="new-transfer__section" aria-labelledby={datesHeadingId}>
              <h3 id={datesHeadingId} className="new-transfer__section-title">
                Dates
                {toZoneLabel ? (
                  <span id={arriveZoneId} className="new-transfer__optional"> · Arrive by is the receiving site&apos;s time ({toZoneLabel})</span>
                ) : null}
              </h3>
              <div className="new-transfer__row">
                <div className="new-transfer__field">
                  <label htmlFor={shipId}>Ship</label>
                  <input id={shipId} type="date" value={shipDay} onChange={(event) => setShipDay(event.target.value)} />
                </div>
                <div className="new-transfer__field">
                  <label htmlFor={arriveId}>Arrive by</label>
                  <input
                    id={arriveId}
                    type="datetime-local"
                    value={arriveBy}
                    aria-describedby={toZoneLabel ? arriveZoneId : undefined}
                    onChange={(event) => setArriveTyped(event.target.value)}
                  />
                </div>
                {scope === 'temporary' ? (
                  <div className="new-transfer__field">
                    <label htmlFor={returnId}>Return by</label>
                    <input id={returnId} type="date" value={returnDay} onChange={(event) => setReturnDay(event.target.value)} />
                  </div>
                ) : null}
              </div>
            </section>

            <section className="new-transfer__section" aria-labelledby={eventHeadingId}>
              <h3 id={eventHeadingId} className="new-transfer__section-title">
                For an event <span className="new-transfer__optional">(optional)</span>
              </h3>
              {linkedEvent ? (
                <span className="new-transfer__event-chip">
                  <strong>{eventTitle(linkedEvent)}</strong>
                  {eventQuery.isPending ? <span>Loading the event…</span> : null}
                  {eventMeta(linkedEvent, siteNames) ? <span>{eventMeta(linkedEvent, siteNames)}</span> : null}
                  <button type="button" aria-label="Remove the event" onClick={() => setEventId(null)}>
                    <CloseIcon />
                  </button>
                </span>
              ) : (
                <EventPicker siteNames={siteNames} onPick={handleEventPick} />
              )}
            </section>

            <section className="new-transfer__section" aria-labelledby={itemsHeadingId}>
              <h3 id={itemsHeadingId} className="new-transfer__section-title">Items</h3>
              <TransferItemSearch
                fromLocationId={fromLocationId || null}
                toLocationId={toLocationId || null}
                fromSiteName={fromSiteName}
                window={searchWindow}
                eventId={eventId}
                onPick={handlePick}
                inputRef={searchInputRef}
              />
              <div className="new-transfer__more">
                <button
                  type="button"
                  className="btn sm subtle"
                  aria-expanded={panel === 'external'}
                  onClick={() => setPanel(panel === 'external' ? 'none' : 'external')}
                >
                  + Item not in the catalog
                </button>
                <button
                  type="button"
                  className="btn sm subtle"
                  aria-expanded={panel === 'vehicle'}
                  onClick={() => setPanel(panel === 'vehicle' ? 'none' : 'vehicle')}
                >
                  + Vehicle
                </button>
              </div>
              {panel === 'external' ? (
                <ExternalLineForm onAdd={handleExternalAdd} onCancel={() => setPanel('none')} />
              ) : null}
              {panel === 'vehicle' ? (
                <VehicleLineForm
                  fromLocationId={fromLocationId || null}
                  fromSiteName={fromSiteName}
                  chosenIds={chosenVehicleIds}
                  onAdd={handleVehicleAdd}
                  onCancel={() => setPanel('none')}
                />
              ) : null}

              {lines.length > 0 ? (
                <div className="new-transfer__lines-wrap">
                  <table className="new-transfer__lines">
                    <caption className="sr-only">Items on this transfer</caption>
                    <thead>
                      <tr>
                        <th scope="col">Item</th>
                        <th scope="col" className="new-transfer__col-free">
                          <span className="new-transfer__wide-label">Free at the From site</span>
                          <span className="new-transfer__narrow-label">Free</span>
                        </th>
                        <th scope="col">Qty</th>
                        <th scope="col" className="new-transfer__col-serials">Serials</th>
                        <th scope="col" className="new-transfer__col-remove">Remove</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((line) => {
                        const free = freeFor(line);
                        const short = free.value != null && line.qty > free.value;
                        const detail = lineDetail(line);
                        const vehicleAway = line.kind === 'vehicle' && line.vehicleSiteId !== fromLocationId;
                        return (
                          <tr key={line.key}>
                            <td className="new-transfer__line-item">
                              <span className="new-transfer__line-name">{line.name}</span>
                              {detail ? <small>{detail}</small> : null}
                              {vehicleAway ? <small className="new-transfer__warn">Not at the From site</small> : null}
                              <small className="new-transfer__line-serials-inline">{serialsText(line)}</small>
                            </td>
                            <td className="new-transfer__col-free">
                              {free.loading ? (
                                <span className="new-transfer__free" aria-label="Loading">…</span>
                              ) : free.value != null ? (
                                <span className="new-transfer__free" data-tone={short ? 'short' : undefined}>
                                  {free.value.toLocaleString()}
                                  {short ? <small>short {(line.qty - free.value).toLocaleString()}</small> : null}
                                </span>
                              ) : (
                                <span className="new-transfer__free" aria-label="Not counted">—</span>
                              )}
                            </td>
                            <td>
                              <QtyStepper
                                value={line.qty}
                                min={Math.max(1, line.assets.length)}
                                label={line.name}
                                disabled={line.kind === 'vehicle'}
                                onChange={(next) => setLineQty(line.key, next)}
                              />
                            </td>
                            <td className="new-transfer__col-serials">{serialsText(line)}</td>
                            <td className="new-transfer__col-remove">
                              <button
                                type="button"
                                className="new-transfer__remove"
                                aria-label={`Remove ${line.name}`}
                                onClick={() => removeLine(line.key)}
                              >
                                <CloseIcon />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {availabilityQuery.isError ? (
                    <p className="new-transfer__note">Free counts could not be loaded. The transfer can still be created.</p>
                  ) : null}
                </div>
              ) : (
                <p className="new-transfer__empty">
                  No items yet. Search above, or add an item that is not in the catalog.
                </p>
              )}
            </section>
          </div>
        </div>

        <ModalActionError message={error?.message ?? null} title={error?.title} hint={error?.hint} />

        <footer className="modal__footer new-transfer__footer">
          <p className="new-transfer__summary" aria-live="polite">{summaryParts.join(' · ')}</p>
          <button
            type="button"
            className="btn btn-primary new-transfer__create"
            onClick={handleCreate}
            disabled={submitting}
            aria-busy={submitting || undefined}
          >
            {submitting ? 'Creating…' : 'Create transfer'}
          </button>
        </footer>
      </div>
    </div>
  );
}

/**
 * New transfer (Warehouse board): route, type, dates, an optional event and items found by name,
 * SKU or serial with what is free at the From site. Not-in-catalog and vehicle lines stay possible.
 * Each opening starts from `initial`; closing discards the draft.
 */
export default function NewTransferDialog({ open, onClose, onCreated, initial = null }: NewTransferDialogProps) {
  if (!open || typeof document === 'undefined') return null;
  return createPortal(
    <NewTransferDialogPanel onClose={onClose} onCreated={onCreated} initial={initial} />,
    document.body,
  );
}
